import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Download, BookOpen, Users, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { LeadCaptureModal } from '../forms/LeadCaptureModal';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ResearchSection = () => {
  const [whitepapers, setWhitepapers] = useState([]);
  const [selectedWhitepaper, setSelectedWhitepaper] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchWhitepapers = async () => {
      try {
        const response = await axios.get(`${API}/whitepapers`);
        setWhitepapers(response.data);
      } catch (error) {
        console.error('Error fetching whitepapers:', error);
      }
    };
    fetchWhitepapers();
  }, []);

  const handleDownloadClick = (whitepaper) => {
    setSelectedWhitepaper(whitepaper);
    setIsModalOpen(true);
  };

  const handleDirectDownload = (url) => {
    window.open(url, '_blank');
  };

  const featuredWhitepaper = whitepapers[0];

  return (
    <section className="py-20 md:py-32 bg-[#F8F9FA]" data-testid="research-section">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-[#3498DB] font-semibold tracking-[0.2em] uppercase text-sm mb-4">
            Research
          </p>
          <h2 className="font-['Montserrat'] font-bold text-3xl md:text-4xl text-[#2C3E50] mb-4">
            Recent Research
          </h2>
          <p className="text-[#64748B] text-lg">
            Strategic analysis and practical guides for enterprise infrastructure leaders.
          </p>
        </div>

        {/* Featured Research Card */}
        {featuredWhitepaper && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden card-hover mb-8" data-testid="featured-research">
            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* Image */}
              <div className="relative h-[250px] md:h-auto">
                <img
                  src={featuredWhitepaper.thumbnail_url}
                  alt={featuredWhitepaper.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-[#3498DB] text-white text-xs font-semibold px-3 py-1 rounded-full">
                    FEATURED RESEARCH
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <h3 className="font-['Montserrat'] font-bold text-xl md:text-2xl text-[#2C3E50] mb-4">
                  {featuredWhitepaper.title}
                </h3>
                <p className="text-[#64748B] mb-6 leading-relaxed">
                  {featuredWhitepaper.description}
                </p>

                {/* Metrics */}
                <div className="flex flex-wrap gap-4 mb-6">
                  <div className="flex items-center gap-2 text-sm text-[#64748B]">
                    <BookOpen size={16} className="text-[#3498DB]" />
                    <span>31 sources cited</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#64748B]">
                    <Users size={16} className="text-[#3498DB]" />
                    <span>100+ enterprise CIOs surveyed</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#64748B]">
                    <FileText size={16} className="text-[#3498DB]" />
                    <span>Implementation roadmap</span>
                  </div>
                </div>

                {/* Topics */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {featuredWhitepaper.topics.map((topic, idx) => (
                    <span
                      key={idx}
                      className="text-xs bg-[#F8F9FA] text-[#64748B] px-3 py-1 rounded-full"
                    >
                      #{topic}
                    </span>
                  ))}
                </div>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/writing"
                    className="inline-flex items-center justify-center gap-2 text-[#3498DB] font-medium hover:gap-3 transition-all duration-200"
                  >
                    Read Analysis <ArrowRight size={16} />
                  </Link>
                  <Button
                    onClick={() => handleDownloadClick(featuredWhitepaper)}
                    variant="outline"
                    className="border-[#2C3E50] text-[#2C3E50] hover:bg-[#2C3E50] hover:text-white"
                    data-testid="download-whitepaper-btn"
                  >
                    <Download size={16} className="mr-2" />
                    Download PDF
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Secondary Links */}
        <div className="flex flex-col sm:flex-row justify-center gap-6">
          <Link
            to="/writing"
            className="inline-flex items-center text-[#2C3E50] font-medium hover:text-[#3498DB] transition-colors duration-200"
          >
            View all research and writing <ArrowRight size={16} className="ml-2" />
          </Link>
          <a
            href="https://siddharthkaul.substack.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-[#2C3E50] font-medium hover:text-[#3498DB] transition-colors duration-200"
          >
            Subscribe to newsletter <ArrowRight size={16} className="ml-2" />
          </a>
        </div>
      </div>

      {/* Lead Capture Modal */}
      <LeadCaptureModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        whitepaper={selectedWhitepaper}
      />
    </section>
  );
};
